import React, { useEffect, useMemo, useState } from "react";
import {
  InfoOutlinedIcon,
  Visibility,
  VisibilityOff,
  ClearIcon,
  TextField,
  InputAdornment,
  IconButton,
  Box,
  Button,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
} from "./MuiImports";
import { useTranslation } from "react-i18next";
import {
  Control,
  Controller,
  FieldErrors,
  set,
  useForm,
  UseFormTrigger,
} from "react-hook-form";
import {
  formatCountryCode,
  formatUSPhoneNumber,
  formatNonUSPhoneNumber,
  formatPhoneNumberFromApi,
} from "../../utils/phoneNumberFormatter";
import styles from "./AuthFields.module.css";
import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";
import InfoPopover from "../templates/selfcare/SelfcareComponents/InfoPopover";
import { MAC_ADDRESS_INFO } from "../templates/selfcare/const/popover-info.const";
import { SELECT_COUNTRY_CODES } from "../../assets/const/countries.const";
import IsFirstRender from "../../utils/isFirstRender";

/** AuthFields are using in BroadbandBand Signup, WiFi Signup, and Selfcare MyInfo page.
 * @param {Control<any>} control - The control object from react-hook-form.
 * @param {FieldErrors} errors - The errors object from react-hook-form.
 * @param {function} onUsernameBlur - Optional callback function triggered when the username input loses focus.
 * @param {function} trigger - Optional trigger function from react-hook-form.
 * @param {string} defaultValue - Optional default value for the field.
 * @param {function} onChange - Optional callback function triggered when the field value changes.
 */

interface AuthFieldsProps {
  control: Control<any>;
  errors: FieldErrors;
  onUsernameBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  trigger?: UseFormTrigger<any>;
  defaultValue?: string;
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
    index?: number,
    name?: string,
    value?: string
  ) => void;
  sx?: any;
  iconStyle?: any;
  isSelfcareForm?: boolean;
  "data-test-id"?: string;
}

interface DeviceFieldProps extends AuthFieldsProps {
  onClick?: (event: React.MouseEvent, index?: number, type?: string) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  index?: number;
  value?: string;
  type?: any;
  isSelected?: boolean;
  validationRules?: any;
}

const authFieldsResources = {
  en: {
    translation: {
      signup_page: {
        username: "Username",
        password: "Password",
        create_username: "Create Username",
        create_password: "Create Password",
        first_name: "First Name",
        last_name: "Last Name",
        email: "Email",
        phone_number: "Phone Number",
        new_password: "New Password",
      },
    },
  },
};

if (!i18n.isInitialized) {
  i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources: authFieldsResources,
      fallbackLng: "en",
      interpolation: {
        escapeValue: false,
      },
    });
}

export const UsernameField: React.FC<AuthFieldsProps> = ({
  control,
  errors,
  onUsernameBlur,
  trigger,
}) => {
  const { t } = useTranslation();
  return (
    <Controller
      name="username"
      control={control}
      defaultValue=""
      rules={{
        required: t("form_errors.username_required", "Username is required"),
        minLength: {
          value: 4,
          message: t(
            "form_errors.username_pattern",
            "Username must be 4-30 characters long and may only contain lower case letters, numbers, periods, and underscores."
          ),
        },
        maxLength: {
          value: 30,
          message: t(
            "form_errors.username_pattern",
            "Usernames must be 4-30 characters long and may only contain lower case letters, numbers, periods, and underscores."
          ),
        },
        pattern: {
          value: /^[a-z0-9._-]+$/,
          message: t(
            "form_errors.username_pattern",
            "Username must be 4-30 characters long and may only contain lower case letters, numbers, periods, and underscores."
          ),
        },
      }}
      render={({ field }) => (
        <TextField
          {...field}
          label={t("signup_page.create_username", {
            defaultValue: "Create Username",
          })}
          fullWidth
          margin="dense"
          error={!!errors.username}
          helperText={errors.username?.message as string | undefined}
          onChange={(e) => {
            const value = e.target.value.slice(0, 30);
            field.onChange(value);
          }}
          onBlur={async (e: React.FocusEvent<HTMLInputElement>) => {
            field.onBlur();
            if (trigger) {
              await trigger("username");
            }
            if (onUsernameBlur && e.target.value) {
              onUsernameBlur(e);
            }
          }}
          className={styles.authField}
          data-test-id="username-field"
          inputProps={{
            "data-test-id": "username-input",
            maxLength: 30,
          }}
        />
      )}
    />
  );
};

export const PasswordField: React.FC<AuthFieldsProps> = ({
  control,
  errors,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const { t } = useTranslation();

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  return (
    <Controller
      name="password"
      control={control}
      defaultValue=""
      rules={{
        required: t("form_errors.password_required", "Password is required"),
        minLength: {
          value: 6,
          message: t(
            "form_errors.password_length",
            "Password must be at least 6-16 characters long"
          ),
        },
      }}
      render={({ field }) => (
        <TextField
          {...field}
          type={showPassword ? "text" : "password"}
          label={t("signup_page.create_password", {
            defaultValue: "Create Password",
          })}
          fullWidth
          margin="dense"
          error={!!errors.password}
          helperText={errors.password?.message as string | undefined}
          className={styles.authField}
          data-test-id="password-field"
          inputProps={{
            "data-test-id": "password-input",
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                  data-test-id="password-visibility-toggle"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      )}
    />
  );
};

export const FirstNameField: React.FC<AuthFieldsProps> = ({
  control,
  errors,
  defaultValue = "",
  onChange,
  isSelfcareForm = false,
  "data-test-id": dataTestId,
}) => {
  const { t } = useTranslation();
  return (
    <Controller
      name="first_name"
      control={control}
      defaultValue={defaultValue}
      rules={{
        required: t(
          isSelfcareForm
            ? "selfcareFormFields.first_name_required"
            : "form_errors.first_name_required",
          "First name is required"
        ),
        pattern: {
          value: /^[a-zA-Z0-9]+$/,
          message: t(
            isSelfcareForm
              ? "selfcareFormFields.first_name_pattern"
              : "form_errors.first_name_pattern",
            "First name should contain only letters or numbers"
          ),
        },
      }}
      render={({ field }) => (
        <TextField
          {...field}
          label={t(
            isSelfcareForm
              ? "selfcareFormFields.first_name"
              : "signup_page.first_name",
            {
              defaultValue: "First Name",
            }
          )}
          fullWidth
          margin="dense"
          error={!!errors.first_name}
          helperText={errors.first_name?.message as string | undefined}
          className={styles.authField}
          data-test-id={dataTestId || "first-name-field"}
          inputProps={{
            "data-test-id": dataTestId
              ? `${dataTestId}-input`
              : "first-name-input",
          }}
          onChange={(e) => {
            field.onChange(e);
            onChange?.(e as React.ChangeEvent<HTMLInputElement>);
          }}
        />
      )}
    />
  );
};

export const LastNameField: React.FC<AuthFieldsProps> = ({
  control,
  errors,
  defaultValue = "",
  onChange,
  isSelfcareForm = false,
  "data-test-id": dataTestId,
}) => {
  const { t } = useTranslation();
  return (
    <Controller
      name="last_name"
      control={control}
      defaultValue={defaultValue}
      rules={{
        required: t(
          isSelfcareForm
            ? "selfcareFormFields.last_name_required"
            : "form_errors.last_name_required",
          "Last name is required"
        ),
        pattern: {
          value: /^[a-zA-Z0-9]+$/,
          message: t(
            isSelfcareForm
              ? "selfcareFormFields.last_name_pattern"
              : "form_errors.last_name_pattern",
            "Last name should contain only letters or numbers"
          ),
        },
      }}
      render={({ field }) => (
        <TextField
          {...field}
          label={t(
            isSelfcareForm
              ? "selfcareFormFields.last_name"
              : "signup_page.last_name",
            {
              defaultValue: "Last Name",
            }
          )}
          fullWidth
          margin="dense"
          error={!!errors.last_name}
          helperText={errors.last_name?.message as string | undefined}
          className={styles.authField}
          data-test-id={dataTestId || "last-name-field"}
          inputProps={{
            "data-test-id": dataTestId
              ? `${dataTestId}-input`
              : "last-name-input",
          }}
          onChange={(e) => {
            field.onChange(e);
            onChange?.(e as React.ChangeEvent<HTMLInputElement>);
          }}
        />
      )}
    />
  );
};

export const EmailField: React.FC<AuthFieldsProps> = ({
  control,
  errors,
  defaultValue = "",
  onChange,
  isSelfcareForm = false,
  "data-test-id": dataTestId,
}) => {
  const { t } = useTranslation();
  return (
    <Controller
      name="email"
      control={control}
      defaultValue={defaultValue}
      rules={{
        required: t(
          isSelfcareForm
            ? "selfcareFormFields.email_required"
            : "form_errors.email_required",
          "Email is required"
        ),
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: t(
            isSelfcareForm
              ? "selfcareFormFields.email_pattern"
              : "form_errors.email_pattern",
            "Invalid email address"
          ),
        },
      }}
      render={({ field }) => (
        <TextField
          {...field}
          type="email"
          label={t(
            isSelfcareForm ? "selfcareFormFields.email" : "signup_page.email",
            {
              defaultValue: "Email",
            }
          )}
          fullWidth
          margin="dense"
          error={!!errors.email}
          helperText={errors.email?.message as string | undefined}
          className={styles.authField}
          data-test-id={dataTestId || "email-field"}
          inputProps={{
            "data-test-id": dataTestId ? `${dataTestId}-input` : "email-input",
          }}
          onChange={(e) => {
            field.onChange(e);
            onChange?.(e as React.ChangeEvent<HTMLInputElement>);
          }}
        />
      )}
    />
  );
};

export const PhoneNumberField: React.FC<AuthFieldsProps> = ({
  control,
  errors,
  trigger,
  defaultValue = "",
  onChange,
  isSelfcareForm = false,
  "data-test-id": dataTestId,
}) => {
  const { t } = useTranslation();
  const isFirstRender = IsFirstRender();
  const [code, setCode] = useState<string>(
    formatCountryCode(SELECT_COUNTRY_CODES.default)
  );
  const [isUSPhoneNumber, setUSPhoneNumber] = useState<boolean>(
    code === formatCountryCode(SELECT_COUNTRY_CODES.default)
  );

  const config = isUSPhoneNumber
    ? {
        maxLength: 12,
        pattern: /\d{3}-\d{3}-\d{4}$/,
      }
    : {
        maxLength: 13, // Non-US Numbers
        pattern: /\d{3}-\d{4}-\d{4}$/,
      };

  // Format the default value from API
  useEffect(() => {
    if (isFirstRender && defaultValue.split(" ").length > 1) {
      const data = Object.values(SELECT_COUNTRY_CODES)
        .filter((entry) => {
          return entry.code.toString() === defaultValue.split(" ")[0];
        })
        .pop();
      data && setUSPhoneNumber(data.code === SELECT_COUNTRY_CODES.default.code);
      data && setCode(formatCountryCode(data));
    }
  }, []);

  // trigger form validation when the country code is changed
  useEffect(() => {
    !isFirstRender && triggerValidation("phone_number");
  }, [code]);

  const triggerValidation = async (field: string) => {
    await trigger!!(field);
  };

  return (
    <Box className={styles.phoneWrapper}>
      <Controller
        name="country_code_verbose"
        control={control}
        render={({ field: { onChange: fieldOnChange, value, ...rest } }) => {
          return (
            <Select
              {...rest}
              labelId="country-code-verbose-dropdown"
              id="country-code-verbose-dropdown"
              label="Country Code"
              className={styles.authField}
              data-test-id={
                dataTestId ? `${dataTestId}-country-code` : undefined
              }
              onChange={async (e) => {
                setCode(e?.target?.value);
                setUSPhoneNumber(
                  e?.target?.value ===
                    formatCountryCode(SELECT_COUNTRY_CODES.default)
                );
                fieldOnChange && fieldOnChange(e.target.value);
                if (onChange) {
                  onChange({
                    ...e,
                    target: {
                      ...e.target,
                      value: e.target.value,
                      name: "country_code_verbose",
                    },
                  } as React.ChangeEvent<HTMLInputElement>);
                }
              }}
              value={code || value}
              sx={{
                borderRadius: "4px 0 0 4px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRight: "none",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderRight: "none",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderRight: "none",
                },
              }}
            >
              {SELECT_COUNTRY_CODES &&
                Object.entries(SELECT_COUNTRY_CODES).map(
                  ([key, value], index) => {
                    return (
                      key !== "default" && (
                        <MenuItem key={index} value={formatCountryCode(value)}>
                          {formatCountryCode(value)}
                        </MenuItem>
                      )
                    );
                  }
                )}
            </Select>
          );
        }}
      />

      <Controller
        name="phone_number"
        control={control}
        defaultValue={formatPhoneNumberFromApi(defaultValue) ?? defaultValue}
        rules={{
          required: t("form_errors.phone_required", "Phone number is required"),
          maxLength: {
            value: config.maxLength,
            message: `Maximum length is ${config.maxLength} characters`,
          },
          pattern: {
            value: config.pattern,
            message: t(
              "form_errors.phone_pattern",
              "Invalid phone number format"
            ),
          },
        }}
        render={({
          field: { onChange: fieldOnChange, value, ...rest },
          fieldState: { error },
        }) => (
          <TextField
            {...rest}
            label={t("signup_page.phone_number", {
              defaultValue: "Phone Number",
            })}
            fullWidth
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "0 4px 4px 0",
                "& fieldset": {
                  borderRadius: "0 4px 4px 0",
                  borderLeft: "1px solid rgba(0, 0, 0, 0.23)",
                },
                "&:hover fieldset": {
                  borderLeft: "1px solid rgba(0, 0, 0, 0.23)",
                },
                "&.Mui-focused fieldset": {
                  borderLeft: "1px solid rgba(0, 0, 0, 0.23)",
                },
              },
            }}
            value={value || ""}
            error={!!error}
            helperText={error ? error.message : ""}
            onChange={async (e) => {
              const formattedValue = isUSPhoneNumber
                ? formatUSPhoneNumber(e.target.value)
                : formatNonUSPhoneNumber(e.target.value);
              fieldOnChange(formattedValue);
              if (onChange) {
                onChange({
                  ...e,
                  target: {
                    ...e.target,
                    value: formattedValue,
                    name: "phone_number",
                  },
                } as React.ChangeEvent<HTMLInputElement>);
              }
            }}
            inputProps={{
              maxLength: config.maxLength,
              "data-test-id": dataTestId
                ? `${dataTestId}-input`
                : "phone-number-input",
            }}
            className={`${styles.authField} ${styles.phoneInput}`}
            data-test-id={dataTestId || "phone-number-field"}
          />
        )}
      />
    </Box>
  );
};

export const SecondPhoneNumberField: React.FC<AuthFieldsProps> = ({
  control,
  errors,
  trigger,
  defaultValue = "",
  onChange,
  isSelfcareForm = false,
  "data-test-id": dataTestId,
}) => {
  const { t } = useTranslation();
  const isFirstRender = IsFirstRender();
  const [code, setCode] = useState<string>(
    formatCountryCode(SELECT_COUNTRY_CODES.default)
  );
  const [isUSPhoneNumber, setUSPhoneNumber] = useState<boolean>(
    code === formatCountryCode(SELECT_COUNTRY_CODES.default)
  );

  // Simplified validation config
  const getValidationConfig = (isUS: boolean) => ({
    maxLength: isUS ? 12 : 13,
    pattern: isUS ? /\d{3}-\d{3}-\d{4}$/ : /\d{3}-\d{4}-\d{4}$/,
  });

  // Initialize country code from default value
  useEffect(() => {
    if (isFirstRender && defaultValue.includes(" ")) {
      const countryCode = defaultValue.split(" ")[0];
      const countryData = Object.values(SELECT_COUNTRY_CODES).find(
        (entry) => entry.code.toString() === countryCode
      );

      if (countryData) {
        const formattedCode = formatCountryCode(countryData);
        setCode(formattedCode);
        setUSPhoneNumber(
          countryData.code === SELECT_COUNTRY_CODES.default.code
        );
      }
    }
  }, [defaultValue, isFirstRender]);

  // Trigger validation when country code changes
  useEffect(() => {
    if (!isFirstRender && trigger) {
      trigger("secondary_phone_number");
    }
  }, [code, isFirstRender, trigger]);

  // Custom validation function for optional phone number
  const validateOptionalPhone = (value: string) => {
    if (!value || value.trim() === "") return true; // Optional field

    const config = getValidationConfig(isUSPhoneNumber);

    if (value.length > config.maxLength) {
      return `Maximum length is ${config.maxLength} characters`;
    }

    if (!config.pattern.test(value)) {
      return t("form_errors.phone_pattern", "Invalid phone number format");
    }

    return true;
  };

  const handleCountryCodeChange = async (newCode: string) => {
    setCode(newCode);
    setUSPhoneNumber(
      newCode === formatCountryCode(SELECT_COUNTRY_CODES.default)
    );

    if (onChange) {
      const syntheticEvent = {
        target: { name: "secondary_country_code_verbose", value: newCode },
        currentTarget: {
          name: "secondary_country_code_verbose",
          value: newCode,
        },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(syntheticEvent);
    }
  };

  const handlePhoneChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldOnChange: (value: string) => void
  ) => {
    const formattedValue = isUSPhoneNumber
      ? formatUSPhoneNumber(e.target.value)
      : formatNonUSPhoneNumber(e.target.value);

    fieldOnChange(formattedValue);

    if (onChange) {
      onChange({
        ...e,
        target: {
          ...e.target,
          value: formattedValue,
          name: "secondary_phone_number",
        },
      } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  return (
    <Box className={styles.phoneWrapper}>
      <Controller
        name="secondary_country_code_verbose"
        control={control}
        render={({ field: { onChange: fieldOnChange, value, ...rest } }) => (
          <Select
            {...rest}
            labelId="secondary-country-code-verbose-dropdown"
            id="secondary-country-code-verbose-dropdown"
            label="Country Code"
            className={styles.authField}
            value={code || value}
            data-test-id={dataTestId ? `${dataTestId}-country-code` : undefined}
            onChange={(e) => {
              fieldOnChange(e.target.value);
              handleCountryCodeChange(e.target.value);
            }}
            sx={{
              borderRadius: "4px 0 0 4px",
              "& .MuiOutlinedInput-notchedOutline": { borderRight: "none" },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderRight: "none",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderRight: "none",
              },
            }}
          >
            {Object.entries(SELECT_COUNTRY_CODES)
              .filter(([key]) => key !== "default")
              .map(([key, value], index) => (
                <MenuItem key={index} value={formatCountryCode(value)}>
                  {formatCountryCode(value)}
                </MenuItem>
              ))}
          </Select>
        )}
      />

      <Controller
        name="secondary_phone_number"
        control={control}
        defaultValue={formatPhoneNumberFromApi(defaultValue) ?? defaultValue}
        rules={{ validate: validateOptionalPhone }}
        render={({
          field: { onChange: fieldOnChange, value, ...rest },
          fieldState: { error },
        }) => (
          <TextField
            {...rest}
            label={t(
              isSelfcareForm
                ? "selfcareFormFields.secondary_phone_number"
                : "signup_page.secondary_phone_number",
              { defaultValue: "Secondary Phone Number (Optional)" }
            )}
            fullWidth
            variant="outlined"
            value={value || ""}
            error={!!error}
            helperText={error?.message || ""}
            onChange={(e) =>
              handlePhoneChange(
                e as React.ChangeEvent<HTMLInputElement>,
                fieldOnChange
              )
            }
            InputProps={{
              endAdornment:
                (value || "").length > 0 ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="clear secondary phone number"
                      onClick={() => {
                        // Clear phone number field
                        fieldOnChange("");

                        // Reset country code to default
                        const defaultCode = formatCountryCode(
                          SELECT_COUNTRY_CODES.default
                        );
                        setCode(defaultCode);
                        setUSPhoneNumber(true);

                        // Trigger onChange events for form state
                        if (onChange) {
                          onChange({
                            target: {
                              name: "secondary_phone_number",
                              value: "",
                            },
                          } as React.ChangeEvent<HTMLInputElement>);

                          onChange({
                            target: {
                              name: "secondary_country_code_verbose",
                              value: defaultCode,
                            },
                          } as React.ChangeEvent<HTMLInputElement>);
                        }
                      }}
                      edge="end"
                      size="small"
                      data-test-id={
                        dataTestId
                          ? `${dataTestId}-clear-button`
                          : "secondary-phone-clear-button"
                      }
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
            }}
            inputProps={{
              maxLength: getValidationConfig(isUSPhoneNumber).maxLength,
              "data-test-id": dataTestId
                ? `${dataTestId}-input`
                : "secondary-phone-number-input",
            }}
            className={`${styles.authField} ${styles.phoneInput}`}
            data-test-id={dataTestId || "secondary-phone-number-field"}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "0 4px 4px 0",
                "& fieldset": {
                  borderRadius: "0 4px 4px 0",
                  borderLeft: "1px solid rgba(0, 0, 0, 0.23)",
                },
                "&:hover fieldset": {
                  borderLeft: "1px solid rgba(0, 0, 0, 0.23)",
                },
                "&.Mui-focused fieldset": {
                  borderLeft: "1px solid rgba(0, 0, 0, 0.23)",
                },
              },
            }}
          />
        )}
      />
    </Box>
  );
};

export const PhoneNumberFieldOptional: React.FC<AuthFieldsProps> = ({
  control,
  errors,
  defaultValue = "",
  onChange,
  "data-test-id": dataTestId,
}) => {
  const { t } = useTranslation();

  const formatPhoneNumber = (value: string): string => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, "");

    // Add dashes after 3rd and 6th digits
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  return (
    <Controller
      name="phone_number_optional"
      control={control}
      defaultValue={defaultValue}
      rules={{
        pattern: {
          value: /^\d{3}-\d{3}-\d{4}$/,
          message: "Phone number format should be XXX-XXX-XXXX",
        },
      }}
      render={({
        field: { onChange: fieldOnChange, value, ...rest },
        fieldState: { error },
      }) => (
        <TextField
          {...rest}
          label={t("selfcareFormFields.phone_optional", {
            defaultValue: "Phone Number (Optional)",
          })}
          fullWidth
          margin="dense"
          value={value || ""}
          error={!!error}
          helperText={error ? error.message : ""}
          onChange={(e) => {
            const formattedValue = formatPhoneNumber(e.target.value);
            fieldOnChange(formattedValue);
            if (onChange) {
              onChange({
                ...e,
                target: {
                  ...e.target,
                  value: formattedValue,
                  name: "phone_number_optional",
                },
              } as React.ChangeEvent<HTMLInputElement>);
            }
          }}
          inputProps={{
            maxLength: 12,
            "data-test-id": dataTestId
              ? `${dataTestId}-input`
              : "phone-number-optional-input",
          }}
          className={styles.authField}
          data-test-id={dataTestId || "phone-number-optional-field"}
        />
      )}
    />
  );
};

export const CityField: React.FC<AuthFieldsProps> = ({
  control,
  errors,
  defaultValue = "",
  onChange,
  "data-test-id": dataTestId,
}) => {
  const { t } = useTranslation();

  return (
    <Controller
      name="city"
      control={control}
      defaultValue={defaultValue}
      rules={{
        pattern: {
          value: /^[a-zA-Z0-9\s]*$/,
          message: "City can only contain letters and numbers",
        },
        maxLength: {
          value: 50,
          message: "City name cannot exceed 50 characters",
        },
      }}
      render={({
        field: { onChange: fieldOnChange, value, ...rest },
        fieldState: { error },
      }) => (
        <TextField
          {...rest}
          label={t("selfcareFormFields.city_optional", {
            defaultValue: "City (Optional)",
          })}
          fullWidth
          margin="dense"
          value={value || ""}
          error={!!error}
          helperText={error ? error.message : ""}
          onChange={(e) => {
            // Only allow letters, numbers, and spaces
            const sanitizedValue = e.target.value.replace(
              /[^a-zA-Z0-9\s]/g,
              ""
            );
            fieldOnChange(sanitizedValue);
            if (onChange) {
              onChange({
                ...e,
                target: {
                  ...e.target,
                  value: sanitizedValue,
                  name: "city",
                },
              } as React.ChangeEvent<HTMLInputElement>);
            }
          }}
          inputProps={{
            maxLength: 50,
            "data-test-id": dataTestId ? `${dataTestId}-input` : "city-input",
          }}
          className={styles.authField}
          data-test-id={dataTestId || "city-field"}
        />
      )}
    />
  );
};

export const StateField: React.FC<AuthFieldsProps> = ({
  control,
  errors,
  defaultValue = "",
  onChange,
  "data-test-id": dataTestId,
}) => {
  const { t } = useTranslation();

  return (
    <Controller
      name="state"
      control={control}
      defaultValue={defaultValue}
      rules={{
        pattern: {
          value: /^[a-zA-Z0-9\s]*$/,
          message: "State can only contain letters and numbers",
        },
        maxLength: {
          value: 2,
          message: "Please use 2-letter state code",
        },
        minLength: {
          value: 2,
          message: "Please use 2-letter state code",
        },
      }}
      render={({
        field: { onChange: fieldOnChange, value, ...rest },
        fieldState: { error },
      }) => (
        <TextField
          {...rest}
          label={t("selfcareFormFields.state_optional", {
            defaultValue: "State (Optional)",
          })}
          fullWidth
          margin="dense"
          value={value ? value.toUpperCase() : ""}
          error={!!error}
          helperText={error ? error.message : ""}
          onChange={(e) => {
            // Only allow letters and numbers, convert to uppercase
            const sanitizedValue = e.target.value
              .replace(/[^a-zA-Z0-9]/g, "")
              .toUpperCase();
            fieldOnChange(sanitizedValue);
            if (onChange) {
              onChange({
                ...e,
                target: {
                  ...e.target,
                  value: sanitizedValue,
                  name: "state",
                },
              } as React.ChangeEvent<HTMLInputElement>);
            }
          }}
          inputProps={{
            maxLength: 2,
            "data-test-id": dataTestId ? `${dataTestId}-input` : "state-input",
            style: { textTransform: "uppercase" },
          }}
          className={styles.authField}
          data-test-id={dataTestId || "state-field"}
        />
      )}
    />
  );
};

export const LoginUsernameField: React.FC<AuthFieldsProps> = ({
  control,
  errors,
  sx,
  isSelfcareForm = false,
}) => {
  const { t } = useTranslation();
  return (
    <Controller
      name="login_username"
      control={control}
      defaultValue=""
      rules={{
        required: t("form_errors.username_required", "Username is required"),
        minLength: {
          value: 4,
          message: t(
            "form_errors.username_min_length",
            "Minimum of 4 characters"
          ),
        },
        pattern: {
          value: /^[a-z0-9._-]+$/,
          message: t(
            "form_errors.username_pattern",
            "Username can only contain lowercase letters, numbers, and . - _ characters"
          ),
        },
      }}
      render={({ field }) => (
        <TextField
          {...field}
          sx={{ ...sx }}
          aria-label={t(
            isSelfcareForm ? "common.username" : "signup_page.username",
            {
              defaultValue: "Username",
            }
          )}
          placeholder={t(
            isSelfcareForm ? "common.username" : "signup_page.username",
            {
              defaultValue: "Username",
            }
          )}
          fullWidth
          margin="dense"
          error={!!errors.login_username}
          helperText={errors.login_username?.message as string | undefined}
          className={styles.authField}
          data-test-id="login-username-field"
          inputProps={{
            "data-test-id": "login-username-input",
          }}
          onChange={(e) => {
            const lowercaseValue = e.target.value.toLowerCase();
            field.onChange(lowercaseValue);
          }}
        />
      )}
    />
  );
};

export const LoginPasswordField: React.FC<AuthFieldsProps> = ({
  control,
  errors,
  sx,
  iconStyle,
  isSelfcareForm = false,
}) => {
  const [loginShowPassword, loginSetShowPassword] = useState(false);
  const { t } = useTranslation();

  const handleClickShowPassword = () => {
    loginSetShowPassword(!loginShowPassword);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  return (
    <Controller
      name="password"
      control={control}
      defaultValue=""
      rules={{
        required: t("form_errors.password_required", "Password is required"),
        minLength: {
          value: 6,
          message: t(
            "form_errors.password_length",
            "Password must be at least 6-16 characters long"
          ),
        },
      }}
      render={({ field }) => (
        <TextField
          {...field}
          sx={{ ...sx }}
          type={loginShowPassword ? "text" : "password"}
          aria-label={t(
            isSelfcareForm
              ? "changePasswordModal.passwordLabel"
              : "signup_page.password",
            {
              defaultValue: "Password",
            }
          )}
          placeholder={t(
            isSelfcareForm
              ? "changePasswordModal.passwordLabel"
              : "signup_page.password",
            {
              defaultValue: "Password",
            }
          )}
          fullWidth
          margin="dense"
          error={!!errors.password}
          helperText={errors.password?.message as string | undefined}
          className={styles.authField}
          data-test-id="login-password-field"
          inputProps={{
            "data-test-id": "login-password-input",
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                  data-test-id="login-password-visibility-toggle"
                  sx={{ ...iconStyle }}
                >
                  {loginShowPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      )}
    />
  );
};

export const NewPasswordField: React.FC<AuthFieldsProps> = ({
  control,
  errors,
  isSelfcareForm = false,
}) => {
  const [showNewPassword, setShowNewPassword] = useState(false);
  const { t } = useTranslation();

  const handleClickShowNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleMouseDownNewPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  return (
    <Controller
      name="new_password"
      control={control}
      defaultValue=""
      rules={{
        required: t("form_errors.password_required", "Password is required"),
        minLength: {
          value: 6,
          message: t(
            "form_errors.password_length",
            "Password must be at least 6-16 characters long"
          ),
        },
      }}
      render={({ field }) => (
        <TextField
          {...field}
          type={showNewPassword ? "text" : "password"}
          label={t(
            isSelfcareForm
              ? "changePasswordModal.newPasswordLabel"
              : "signup_page.new_password",
            {
              defaultValue: "New Password",
            }
          )}
          fullWidth
          margin="dense"
          error={!!errors.new_password}
          helperText={errors.new_password?.message as string | undefined}
          className={styles.authField}
          data-test-id="new-password-field"
          inputProps={{
            "data-test-id": "new-password-input",
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowNewPassword}
                  onMouseDown={handleMouseDownNewPassword}
                  edge="end"
                  data-test-id="new-password-visibility-toggle"
                >
                  {showNewPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      )}
    />
  );
};

export const MacAddressField: React.FC<DeviceFieldProps> = ({
  control,
  errors,
  onChange,
  onBlur,
  index,
  value,
  validationRules,
  "data-test-id": dataTestId,
}) => {
  const { t } = useTranslation();
  const [infoAnchorEl, setInfoAnchorEl] = useState<HTMLElement | null>(null);
  const [activeInfoIndex, setActiveInfoIndex] = useState<number | null>(null);

  const handleInfoClick = (
    event: React.MouseEvent<HTMLElement>,
    index: number
  ) => {
    setInfoAnchorEl(event.currentTarget);
    setActiveInfoIndex(index);
  };

  const handleInfoClose = () => {
    setInfoAnchorEl(null);
    setActiveInfoIndex(null);
  };

  const macAddressPopover = useMemo(
    () => (
      <InfoPopover
        open={Boolean(infoAnchorEl)}
        anchorEl={infoAnchorEl}
        onClose={handleInfoClose}
        content={MAC_ADDRESS_INFO}
      />
    ),
    [infoAnchorEl]
  );

  return (
    <Controller
      name={`mac-${index}`}
      control={control}
      defaultValue=""
      rules={
        validationRules || {
          required: t("myDevicesPage.macAddressRequired"),
          minLength: {
            value: 12,
            message: t("myDevicesPage.macAddressFormat"),
          },
        }
      }
      render={({ field }) => (
        <>
          <TextField
            {...field}
            label={t("myDevicesPage.macAddressLabel")}
            fullWidth
            margin="dense"
            value={value && value}
            error={!!errors[`mac-${index}`]}
            helperText={errors[`mac-${index}`]?.message as string | undefined}
            className={styles.authField}
            onBlur={onBlur && onBlur}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChange && onChange(e, index, "mac", e.target.value)
            }
            FormHelperTextProps={{
              sx: { marginLeft: "0" },
            }}
            data-test-id={dataTestId}
            inputProps={{
              "data-test-id": dataTestId ? `${dataTestId}-input` : undefined,
            }}
            InputProps={{
              endAdornment: (
                <IconButton
                  size="small"
                  onClick={(e) => index && handleInfoClick(e, index)}
                  aria-label={t("myDevicesPage.macAddressInfo")}
                  data-test-id={
                    dataTestId ? `${dataTestId}-info-button` : undefined
                  }
                >
                  <InfoOutlinedIcon />
                </IconButton>
              ),
            }}
          />
          {macAddressPopover}
        </>
      )}
    />
  );
};

export const DeviceNameField: React.FC<DeviceFieldProps> = ({
  control,
  errors,
  onChange,
  onBlur,
  index,
  value,
  validationRules,
  "data-test-id": dataTestId,
}) => {
  const { t } = useTranslation();
  return (
    <Controller
      name={`name-${index}`}
      control={control}
      defaultValue=""
      rules={
        validationRules || {
          required: t("myDevicesPage.deviceNameRequired"),
        }
      }
      render={({ field }) => (
        <TextField
          {...field}
          label={t("myDevicesPage.deviceNameLabel")}
          fullWidth
          margin="dense"
          value={value && value}
          error={!!errors[`name-${index}`]}
          helperText={errors[`name-${index}`]?.message as string | undefined}
          className={styles.authField}
          onBlur={onBlur && onBlur}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange && onChange(e, index, "name", e.target.value)
          }
          FormHelperTextProps={{
            sx: { marginLeft: "0" },
          }}
          data-test-id={dataTestId}
          inputProps={{
            "data-test-id": dataTestId
              ? `${dataTestId}-input`
              : "device-name-input",
          }}
        />
      )}
    />
  );
};

export const DeviceTypeField: React.FC<DeviceFieldProps> = ({
  control,
  errors,
  onClick,
  type,
  index,
  isSelected,
  validationRules,
  "data-test-id": dataTestId,
}) => {
  return (
    <Controller
      name={`type-${index}`}
      control={control}
      defaultValue=""
      rules={validationRules}
      render={({ field }) => (
        <Button
          {...field}
          variant="outlined"
          onClick={onClick && onClick}
          name={type.model}
          data-test-id={dataTestId}
          sx={{
            border: "1px solid #ccc",
            color: isSelected ? "#d32f2f" : "inherit",
            borderColor: isSelected ? "#d32f2f" : "#ccc",
            textTransform: "none",
            fontSize: 12,
            height: "70px",
            flex: {
              xs: "0 0 calc(33.333% - 8px)",
              sm: "1 1 0",
            },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: isSelected ? "rgba(211, 47, 47, 0.04)" : "inherit",
            "&:hover": {
              backgroundColor: isSelected
                ? "rgba(211, 47, 47, 0.08)"
                : "rgba(0, 0, 0, 0.04)",
              borderColor: isSelected ? "#d32f2f" : "#ccc",
            },
          }}
        >
          <Box className={type.icon} sx={{ fontSize: "40px" }} />
          {type.name}
        </Button>
      )}
    />
  );
};

export const PromoCodeField: React.FC<AuthFieldsProps> = ({
  control,
  errors,
  defaultValue = "",
  onChange,
}) => {
  return (
    <Controller
      name="promo_code"
      control={control}
      defaultValue={defaultValue}
      rules={{
        pattern: {
          value: /^[A-Za-z0-9_]+$/,
          message:
            "Promo code can only contain letters, numbers, and underscore",
        },
      }}
      render={({ field }) => (
        <TextField
          {...field}
          label="Enter Discount Code"
          fullWidth
          margin="dense"
          error={!!errors.promo_code}
          helperText={errors.promo_code?.message as string | undefined}
          className={styles.authField}
          data-test-id="promo-code-field"
          inputProps={{
            "data-test-id": "promo-code-input",
          }}
          onChange={(e) => {
            field.onChange(e);
            onChange?.(e as React.ChangeEvent<HTMLInputElement>);
          }}
        />
      )}
    />
  );
};
